import {
	ComponentDefinition,
	ComponentKey,
	StyleField,
	TemplateDesign,
	TextKey,
	VariantOption,
} from '../../types';
import { CTA_ICON_OPTIONS } from './cta-button/icons';

/** "OFFER ENDS IN" label of the text countdowns (Lato Bold 12). */
const COUNTDOWN_OFFER_LABEL = {
	text: 'OFFER ENDS IN',
	fontFamily: 'Lato',
	weight: '700',
	size: '12',
	lineHeight: '1',
	align: 'center' as const,
};

/** "Limited Time Offer" label (Poppins Medium, navy). */
const COUNTDOWN_LIMITED_LABEL = {
	text: 'Limited Time Offer',
	fontFamily: 'Poppins',
	weight: '500',
	size: '16',
	lineHeight: 'normal',
	color: '#042853',
	align: 'center' as const,
};

/** Digits of the text countdowns (Poppins SemiBold 28). */
const COUNTDOWN_TEXT_DIGITS = {
	fontFamily: 'Poppins',
	weight: '600',
	size: '28',
	lineHeight: '1',
};

/** Text styles of the ticket, dashed and pill coupons (Template 1 design). */
const COUPON_TICKET_TEXT: NonNullable<
	VariantOption[ 'preset' ]
>[ 'typography' ] = {
	couponCopyLabel: {
		text: 'Copy Coupon Code',
		fontFamily: 'Lato',
		weight: '700',
		size: '12',
		lineHeight: '1',
		color: '#2E4450',
		align: 'center',
	},
	couponCode: {
		fontFamily: 'Lato',
		weight: '700',
		size: '16',
		lineHeight: '1',
		color: '#FFFFFF',
	},
	couponCopiedText: {
		fontFamily: 'Lato',
		weight: '700',
		size: '16',
		lineHeight: '1',
		color: '#FFFFFF',
	},
};

/**
 * Padding field (four sides, px). Empty sides keep the style's padding.
 *
 * @param label    Field label.
 * @param defaults The style's own padding per variant ("*" = all).
 * @param extra    Other field options (variants, help).
 */
const paddingField = (
	label: string,
	defaults: Record< string, string >,
	extra: Partial< StyleField > = {}
): StyleField => ( {
	key: 'padding',
	label,
	type: 'padding',
	min: 0,
	max: 300,
	paddingDefaults: defaults,
	group: 'Spacing',
	...extra,
} );

/**
 * Margin field (four sides, px, may be negative): space outside.
 *
 * @param extra Other field options (label, variants).
 */
const marginField = ( extra: Partial< StyleField > = {} ): StyleField => ( {
	key: 'margin',
	label: 'Margin',
	type: 'margin',
	min: -200,
	max: 200,
	paddingDefaults: { '*': '0 0 0 0' },
	group: 'Spacing',
	...extra,
} );

/**
 * Hover color (empty = no change on hover).
 *
 * @param key      Hover key (see getHoverProps).
 * @param label    Field label.
 * @param variants Only for these variants.
 */
const hoverField = (
	key: 'hoverText' | 'hoverBg' | 'hoverBorder' | 'hoverLabel' | 'hoverArea',
	label: string,
	variants?: string[]
): StyleField => ( {
	key,
	label,
	type: 'optionalColor',
	group: 'Hover',
	variants,
} );

/** Underline of text links. */
const linkUnderlineField: StyleField = {
	key: 'linkUnderline',
	label: 'Link underline',
	type: 'select',
	group: 'Link',
	options: [
		{ value: 'hover', label: 'On hover' },
		{ value: 'always', label: 'Always' },
		{ value: 'none', label: 'Never' },
	],
};

/** Link input of a text (its own link, not the CTA's). */
const TEXT_LINK = { showLink: true, linkLabel: 'Link (optional)' };

/** Coupon styles with a code box (all but the plain-text one). */
const COUPON_BOX = [
	'ticket',
	'card',
	'pillcopy',
	'glossy',
	'dashed',
	'pill',
	'gradpill',
	'dashcopy',
];
const COUPON_BOX_SIZED = COUPON_BOX.filter( ( id ) => id !== 'ticket' );
const BOX_BG = ( values: string[] ) => ( { key: 'boxBgType', values } );
const BOX_BORDER_SET = {
	key: 'boxBorderStyle',
	values: [ 'solid', 'dashed', 'dotted' ],
};

/**
 * A single text with plain / badge looks (used by "First Text" and "Text").
 *
 * @param key         Component key.
 * @param label       Component name.
 * @param description Component help.
 * @param textKey     Its text element.
 */
const textComponent = (
	key: 'preText' | 'text' | 'headline' | 'fourthText',
	label: string,
	description: string,
	textKey: 'preText' | 'extraText' | 'highlight' | 'secondary'
): ComponentDefinition => ( {
	key,
	label,
	description,
	canHide: true,
	variants: [
		{ id: 'plain', label: 'Plain' },
		{ id: 'badge', label: 'Badge' },
	],
	styleFields: [
		{
			key: 'bgColor',
			label: 'Badge background',
			type: 'color',
			variants: [ 'badge' ],
		},
		{
			key: 'radius',
			label: 'Badge corner radius',
			type: 'number',
			unit: 'px',
			min: 0,
			max: 40,
			variants: [ 'badge' ],
		},
		paddingField( 'Padding', { plain: '0 0 0 0', badge: '4 14 4 14' } ),
		marginField(),
		hoverField( 'hoverText', 'Text color' ),
		hoverField( 'hoverBg', 'Background' ),
		linkUnderlineField,
	],
	textFields: [ { key: textKey, label, hideAlign: true, ...TEXT_LINK } ],
	mediaFields: [],
} );

/** CTA styles with the plain hover colors (the bordered one has its own). */
const CTA_SIMPLE_HOVER = [ 'gradient', 'solid', 'green', 'outline', 'glass' ];

/**
 * Components of the base template, in their fixed banner order.
 * Each one can be shown/hidden (except the background), offers design
 * variations and the style fields for the selected variation.
 */
export const COMPONENTS: ComponentDefinition[] = [
	{
		key: 'background',
		label: 'Banner & Background',
		description: 'Banner height, spacing between items and background.',
		canHide: false,
		variants: [
			{ id: 'image', label: 'Image' },
			{ id: 'gradient', label: 'Gradient' },
			{ id: 'solid', label: 'Solid color' },
		],
		styleFields: [
			{
				key: 'height',
				label: 'Min height',
				type: 'number',
				unit: 'px',
				min: 40,
				max: 200,
			},
			{
				key: 'gap',
				label: 'Space between items',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 120,
			},
			{
				key: 'color',
				label: 'Background color',
				type: 'color',
				variants: [ 'solid', 'image' ],
			},
			{
				key: 'gradientFrom',
				label: 'Gradient start',
				type: 'color',
				variants: [ 'gradient' ],
			},
			{
				key: 'gradientTo',
				label: 'Gradient end',
				type: 'color',
				variants: [ 'gradient' ],
			},
			{
				key: 'gradientAngle',
				label: 'Gradient angle',
				type: 'number',
				unit: '°',
				min: 0,
				max: 360,
				variants: [ 'gradient' ],
			},
			{
				key: 'textGap',
				label: 'Space between texts (empty = 6)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 80,
			},
			paddingField(
				'Content padding',
				{ '*': '6 200 6 200' },
				{
					help: 'Space around the items. Left/right shrink to 32px on screens narrower than 1280px.',
				}
			),
		],
		textFields: [],
		mediaFields: [
			{
				key: 'bgImage',
				label: 'Background Image',
				description:
					'Covers the whole banner. Width/height set the background size (Auto = cover).',
				variants: [ 'image' ],
			},
		],
	},
	{
		key: 'leftImage',
		label: 'Left Image',
		description: 'Decorative image on the left edge.',
		canHide: true,
		variants: [ { id: 'image', label: 'Image' } ],
		styleFields: [
			{
				key: 'offset',
				label: 'Distance from edge',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 200,
			},
		],
		textFields: [],
		mediaFields: [
			{
				key: 'leftIcon',
				label: 'Left Image',
				description: 'Hidden on screens narrower than 1200px.',
			},
		],
	},
	{
		key: 'rightImage',
		label: 'Right Image',
		description: 'Decorative image on the right edge.',
		canHide: true,
		variants: [ { id: 'image', label: 'Image' } ],
		styleFields: [
			{
				key: 'offset',
				label: 'Distance from edge',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 200,
			},
		],
		textFields: [],
		mediaFields: [
			{
				key: 'rightIcon',
				label: 'Right Image',
				description: 'Hidden on screens narrower than 1200px.',
			},
		],
	},
	// Four separate texts, left to right, each with every text option.
	textComponent(
		'preText',
		'First Text',
		'Optional first text, e.g. a "NEW" badge. Empty = not shown.',
		'preText'
	),
	textComponent(
		'text',
		'Second Text',
		'Second text, e.g. "Autumn Sale!".',
		'extraText'
	),
	textComponent(
		'headline',
		'Third Text',
		'Third text, e.g. "Save 80%".',
		'highlight'
	),
	textComponent(
		'fourthText',
		'Fourth Text',
		'Fourth text, e.g. "On Single Courses.".',
		'secondary'
	),
	{
		key: 'coupon',
		label: 'Coupon',
		description: 'Coupon code visitors can click to copy.',
		canHide: true,
		variantPicker: { type: 'select', label: 'Coupon style' },
		variants: [
			{
				id: 'ticket',
				label: 'Style 1 · Ticket',
				preset: { typography: COUPON_TICKET_TEXT },
			},
			{
				// Figma "coupon v2" (node 10062:6517).
				id: 'card',
				label: 'Style 2 · Card',
				preset: {
					style: {
						cardColor: '#FFFFFF',
						accentColor: '#006D5B',
						codeBgColor: '#E7E9F2',
					},
					typography: {
						couponCopyLabel: {
							text: 'COUPON:',
							fontFamily: 'Source Sans 3',
							weight: '600',
							size: '18',
							lineHeight: 'normal',
							color: '#222222',
							align: 'left',
						},
						couponCode: {
							fontFamily: 'Source Sans 3',
							weight: '600',
							size: '20',
							lineHeight: '1.2',
							color: '#006D5B',
						},
						couponCopiedText: {
							fontFamily: 'Source Sans 3',
							weight: '600',
							size: '20',
							lineHeight: '1.2',
							color: '#006D5B',
						},
					},
				},
			},
			{
				// Figma "coupon v3" (node 10062:6559).
				id: 'pillcopy',
				label: 'Style 3 · Pill with copy button',
				preset: {
					style: { copyBgColor: '#FEF4DD' },
					typography: {
						couponCopyLabel: {
							text: 'COPY',
							fontFamily: 'Poppins',
							weight: '600',
							size: '16',
							lineHeight: 'normal',
							color: '#37506E',
							align: 'center',
						},
						couponCode: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '22',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
						couponCopiedText: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '22',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// Figma "coupon v4" (node 10062:6633).
				id: 'glossy',
				label: 'Style 4 · Glossy button',
				preset: {
					typography: {
						couponCopyLabel: {
							text: 'Copy Coupon',
							fontFamily: 'Poppins',
							weight: '400',
							size: '18',
							lineHeight: 'normal',
							color: '#042853',
							align: 'center',
						},
						couponCode: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '20',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
						couponCopiedText: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '20',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// Figma "coupon v6" (node 10062:7892).
				id: 'inline',
				label: 'Style 5 · Inline text',
				preset: {
					typography: {
						couponCopyLabel: {
							text: 'Copy Coupon:',
							fontFamily: 'Poppins',
							weight: '500',
							size: '22',
							lineHeight: 'normal',
							color: '#2E4450',
							align: 'left',
						},
						couponCode: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '22',
							lineHeight: 'normal',
							color: '#D6185B',
						},
						couponCopiedText: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '22',
							lineHeight: 'normal',
							color: '#D6185B',
						},
					},
				},
			},
			{
				id: 'dashed',
				label: 'Style 6 · Dashed',
				preset: { typography: COUPON_TICKET_TEXT },
			},
			{
				id: 'pill',
				label: 'Style 7 · Pill',
				preset: { typography: COUPON_TICKET_TEXT },
			},
			{
				// Figma dark dashed box with copy button (node 10343:1113).
				id: 'dashcopy',
				label: 'Style 9 · Dark dashed + copy',
				preset: {
					style: {
						bgColor: '#01241D',
						borderColor: '#E2F995',
						copyBgColor: '#ACF956',
					},
					typography: {
						// The design has no label above the box.
						couponCopyLabel: { isVisible: false },
						couponCode: {
							fontFamily: 'Poppins',
							weight: '700',
							size: '13.45',
							lineHeight: 'normal',
							color: '#E2F995',
						},
						couponCopiedText: {
							fontFamily: 'Poppins',
							weight: '700',
							size: '13.45',
							lineHeight: 'normal',
							color: '#E2F995',
						},
						couponCopyButton: {
							text: 'COPY',
							fontFamily: 'Poppins',
							weight: '600',
							size: '10.34',
							lineHeight: 'normal',
							color: '#01241D',
							align: 'center',
						},
					},
				},
			},
			{
				// Figma "Use Code" pill (node 10343:1096), for dark banners.
				id: 'gradpill',
				label: 'Style 8 · Gradient pill + copy',
				previewBg: '#0D1B5E',
				preset: {
					style: {
						pillFrom: '#FF9A48',
						pillTo: '#DA3A00',
						copyBgColor: '#030B32',
					},
					typography: {
						couponCopyLabel: {
							text: 'Use Code',
							fontFamily: 'Inter',
							weight: '400',
							size: '12',
							lineHeight: 'normal',
							color: '#FFFFFF',
							align: 'center',
						},
						couponCode: {
							fontFamily: 'Inter',
							weight: '600',
							size: '14',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
						couponCopiedText: {
							fontFamily: 'Inter',
							weight: '600',
							size: '14',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
						couponCopyButton: {
							text: 'COPY',
							fontFamily: 'Inter',
							weight: '600',
							size: '12',
							lineHeight: 'normal',
							color: '#FFFFFF',
							align: 'center',
						},
					},
				},
			},
		],
		styleFields: [
			{
				key: 'bgColor',
				label: 'Background',
				type: 'color',
				variants: [ 'dashed', 'pill', 'dashcopy' ],
			},
			{
				key: 'borderColor',
				label: 'Dashed line',
				type: 'color',
				variants: [ 'dashed', 'dashcopy' ],
			},
			{
				key: 'cardColor',
				label: 'Card background',
				type: 'color',
				variants: [ 'card' ],
			},
			{
				key: 'accentColor',
				label: 'Accent (edge & dashed border)',
				type: 'color',
				variants: [ 'card' ],
			},
			{
				key: 'codeBgColor',
				label: 'Code background',
				type: 'color',
				variants: [ 'card' ],
			},
			{
				key: 'copyBgColor',
				label: 'Copy button background',
				type: 'color',
				variants: [ 'pillcopy', 'gradpill', 'dashcopy' ],
			},
			{
				key: 'pillFrom',
				label: 'Pill gradient start',
				type: 'color',
				variants: [ 'gradpill' ],
			},
			{
				key: 'pillTo',
				label: 'Pill gradient end',
				type: 'color',
				variants: [ 'gradpill' ],
			},
			paddingField(
				'Code box padding',
				{
					card: '0 16 0 16',
					pillcopy: '0 4 0 18',
					glossy: '0 24 0 30',
					dashed: '0 18 0 18',
					pill: '0 22 0 22',
					gradpill: '0 3 0 10',
					dashcopy: '6 12 6 12',
				},
				{
					variants: [
						'card',
						'pillcopy',
						'glossy',
						'dashed',
						'pill',
						'gradpill',
						'dashcopy',
					],
				}
			),
			// Code box: background, border, corners and size (any style).
			{
				key: 'boxBgType',
				label: 'Background',
				type: 'select',
				group: 'Code box',
				variants: COUPON_BOX,
				options: [
					{ value: 'default', label: 'Style default' },
					{ value: 'color', label: 'Color' },
					{ value: 'gradient', label: 'Gradient' },
					{ value: 'image', label: 'Image' },
					{ value: 'none', label: 'None (transparent)' },
				],
			},
			{
				key: 'boxBgColor',
				label: 'Background color',
				type: 'color',
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BG( [ 'color' ] ),
			},
			{
				key: 'boxGradFrom',
				label: 'Gradient start',
				type: 'color',
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BG( [ 'gradient' ] ),
			},
			{
				key: 'boxGradTo',
				label: 'Gradient end',
				type: 'color',
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BG( [ 'gradient' ] ),
			},
			{
				key: 'boxGradAngle',
				label: 'Gradient angle',
				type: 'number',
				unit: '°',
				min: 0,
				max: 360,
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BG( [ 'gradient' ] ),
			},
			{
				key: 'boxBorderStyle',
				label: 'Border',
				type: 'select',
				group: 'Code box',
				variants: COUPON_BOX,
				options: [
					{ value: 'default', label: 'Style default' },
					{ value: 'none', label: 'No border' },
					{ value: 'solid', label: 'Solid' },
					{ value: 'dashed', label: 'Dashed' },
					{ value: 'dotted', label: 'Dotted' },
				],
			},
			{
				key: 'boxBorderColor',
				label: 'Border color',
				type: 'color',
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BORDER_SET,
			},
			{
				key: 'boxBorderWidth',
				label: 'Border width',
				type: 'number',
				unit: 'px',
				min: 1,
				max: 10,
				group: 'Code box',
				variants: COUPON_BOX,
				showWhen: BOX_BORDER_SET,
			},
			{
				key: 'boxRadius',
				label: 'Corner radius (empty = style)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 60,
				group: 'Code box',
				variants: COUPON_BOX,
			},
			{
				key: 'boxWidth',
				label: 'Min width (empty = style)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 400,
				group: 'Code box',
				variants: COUPON_BOX_SIZED,
			},
			{
				key: 'boxHeight',
				label: 'Min height (empty = style)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 120,
				group: 'Code box',
				variants: COUPON_BOX_SIZED,
			},
			// Coupon area: the whole coupon (label + code box).
			{
				key: 'areaBg',
				label: 'Background (empty = none)',
				type: 'optionalColor',
				group: 'Coupon area',
				variants: [
					'ticket',
					'pillcopy',
					'glossy',
					'dashed',
					'pill',
					'inline',
				],
			},
			{
				key: 'areaRadius',
				label: 'Corner radius',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 60,
				group: 'Coupon area',
			},
			paddingField(
				'Area padding',
				{ '*': '0 0 0 0', card: '7 7 7 10' },
				{ key: 'areaPadding', group: 'Coupon area' }
			),
			marginField(),
			// Hovering anywhere on the coupon (label or code) applies these.
			{
				key: 'hoverEffect',
				label: 'Hover effect',
				type: 'select',
				group: 'Hover',
				options: [
					{ value: 'dim', label: 'Fade slightly (default)' },
					{ value: 'lift', label: 'Lift with shadow' },
					{ value: 'grow', label: 'Grow' },
					{ value: 'none', label: 'None' },
				],
			},
			hoverField( 'hoverBg', 'Code box background', COUPON_BOX ),
			hoverField( 'hoverText', 'Code text' ),
			hoverField( 'hoverBorder', 'Border', COUPON_BOX ),
			hoverField( 'hoverLabel', 'Label text' ),
			hoverField( 'hoverArea', 'Area background' ),
		],
		textFields: [
			{
				key: 'couponCopyLabel',
				label: 'Copy Label',
				hideAlign: [ 'card', 'inline', 'pillcopy' ],
			},
			{
				key: 'couponCode',
				label: 'Coupon Code',
				hideAlign: [ 'inline' ],
			},
			{
				key: 'couponCopiedText',
				label: 'Copied Message',
				hideAlign: [ 'inline' ],
			},
			{
				key: 'couponCopyButton',
				label: 'Copy Button',
				hideAlign: true,
				hideForVariants: [
					'ticket',
					'card',
					'pillcopy',
					'glossy',
					'inline',
					'dashed',
					'pill',
				],
			},
		],
		mediaFields: [
			{
				key: 'couponBg',
				label: 'Ticket Image',
				description: 'Image behind the coupon code.',
				variants: [ 'ticket' ],
			},
			{
				key: 'couponBoxBg',
				label: 'Code box image',
				description:
					'Background image of the code box. Width/height set its size (Auto = cover).',
				variants: COUPON_BOX,
				showWhen: BOX_BG( [ 'image' ] ),
			},
		],
	},
	{
		key: 'countdown',
		label: 'Countdown',
		description: 'Live timer. The end date is set per banner.',
		canHide: true,
		variantPicker: { type: 'select', label: 'Countdown style' },
		// Designs from the Figma "fb inspiration" floating bars.
		variants: [
			{
				// Autumn Sale bar.
				id: 'text',
				label: 'Style 1 · Text (red)',
				preset: {
					typography: {
						countdownLabel: {
							...COUNTDOWN_OFFER_LABEL,
							color: '#2E4450',
						},
						countdownDigits: {
							...COUNTDOWN_TEXT_DIGITS,
							color: '#AF222B',
						},
					},
				},
			},
			{
				// Halloween bar (dark background).
				id: 'textlight',
				label: 'Style 2 · Text on dark (gold)',
				previewBg: '#0B3D2C',
				preset: {
					typography: {
						countdownLabel: {
							...COUNTDOWN_OFFER_LABEL,
							color: '#FFFFFF',
						},
						countdownDigits: {
							...COUNTDOWN_TEXT_DIGITS,
							color: '#FFC300',
						},
					},
				},
			},
			{
				// October Safety Sale bar.
				id: 'textnavy',
				label: 'Style 3 · Text (navy)',
				preset: {
					typography: {
						countdownLabel: {
							...COUNTDOWN_LIMITED_LABEL,
							size: '12',
						},
						countdownDigits: {
							...COUNTDOWN_TEXT_DIGITS,
							color: '#042853',
						},
					},
				},
			},
			{
				// Winter / October bars ("coupon v5", node 10062:6657).
				id: 'notched',
				label: 'Style 4 · Notched boxes',
				preset: {
					typography: {
						countdownLabel: COUNTDOWN_LIMITED_LABEL,
						countdownDigits: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '20',
							lineHeight: '1',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// Spring bar.
				id: 'flip',
				label: 'Style 5 · Flip tiles',
				preset: {
					style: {
						tileColor: '#1BAA76',
						digitColor: '#F2F2F2',
						unitColor: '#222222',
					},
					typography: {
						countdownLabel: {
							text: 'TIME REMAINING',
							fontFamily: 'Lato',
							weight: '600',
							size: '12',
							lineHeight: '1',
							color: '#222222',
							align: 'center',
						},
					},
				},
			},
			{
				id: 'boxes',
				label: 'Style 6 · Boxes with units',
				preset: {
					typography: {
						countdownLabel: {
							...COUNTDOWN_OFFER_LABEL,
							color: '#2E4450',
						},
					},
				},
			},
		],
		styleFields: [
			{
				key: 'boxColor',
				label: 'Box color',
				type: 'color',
				variants: [ 'boxes' ],
			},
			{
				key: 'boxDigitColor',
				label: 'Digit color',
				type: 'color',
				variants: [ 'boxes' ],
			},
			{
				key: 'tileColor',
				label: 'Tile color',
				type: 'color',
				variants: [ 'flip' ],
			},
			{
				key: 'digitColor',
				label: 'Digit color',
				type: 'color',
				variants: [ 'flip' ],
			},
			{
				key: 'unitColor',
				label: 'Unit label color',
				type: 'color',
				variants: [ 'boxes', 'flip' ],
			},
			paddingField( 'Padding', { '*': '0 0 0 0' } ),
			marginField(),
		],
		textFields: [
			{ key: 'countdownLabel', label: 'Countdown Label' },
			{
				key: 'countdownDigits',
				label: 'Timer Digits',
				hideText: true,
				hideAlign: [ 'notched', 'boxes' ],
				// The flip tiles have a fixed digit design (Digit color above).
				hideForVariants: [ 'flip' ],
			},
		],
		mediaFields: [],
	},
	{
		key: 'cta',
		label: 'CTA Button',
		description:
			'Call-to-action button. Its text and link belong to this banner.',
		canHide: true,
		variantPicker: { type: 'select', label: 'Button style' },
		// Designs from the Figma floating bars.
		variants: [
			{
				// Autumn Sale bar.
				id: 'gradient',
				label: 'Style 1 · Red gradient',
				preset: {
					style: {
						gradientFrom: '#ED4500',
						gradientTo: '#BF090A',
						radius: '7.5',
						width: '150',
						height: '41',
					},
					typography: {
						cta: {
							fontFamily: 'Poppins',
							weight: '500',
							size: '18',
							lineHeight: '1',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// October Safety Sale bar (node 10062:6649).
				id: 'solid',
				label: 'Style 2 · Solid slate',
				preset: {
					style: {
						bgColor: '#37506E',
						radius: '8',
						width: '0',
						height: '46',
					},
					typography: {
						cta: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '20',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// Summer Super Sale bar (node 10062:6522).
				id: 'green',
				label: 'Style 3 · Solid green',
				preset: {
					style: {
						bgColor: '#006D5B',
						radius: '8',
						width: '0',
						height: '41',
					},
					typography: {
						cta: {
							fontFamily: 'Lato',
							weight: '700',
							size: '20',
							lineHeight: '1.3',
							color: '#F5F5F5',
						},
					},
				},
			},
			{
				// New Season bar (node 10062:6865): light button, dark on hover.
				id: 'bordered',
				label: 'Style 4 · Light with border',
				preset: {
					style: {
						bgColor: '#84E3FE',
						borderColor: '#092FC3',
						hoverBgColor: '#3F4D97',
						hoverTextColor: '#FFFFFF',
						radius: '7.5',
						width: '150',
						height: '41',
					},
					typography: {
						cta: {
							fontFamily: 'Poppins',
							weight: '500',
							size: '18',
							lineHeight: 'normal',
							color: '#D6185B',
						},
					},
				},
			},
			{
				// New Year bar (node 10062:12054), for dark banners.
				id: 'outline',
				label: 'Style 5 · Outline',
				previewBg: '#0D1B5E',
				preset: {
					style: {
						borderColor: '#FF0000',
						radius: '7.5',
						width: '0',
						height: '39',
					},
					typography: {
						cta: {
							fontFamily: 'Poppins',
							weight: '500',
							size: '22',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
					},
				},
			},
			{
				// Halloween bar (node 10062:6427), for dark banners.
				id: 'glass',
				label: 'Style 6 · Glass gradient',
				previewBg: '#0B3D2C',
				preset: {
					style: {
						glassFrom: '#00CCFF',
						glassTo: '#00FF3A',
						radius: '0',
						width: '0',
						height: '47',
					},
					typography: {
						cta: {
							fontFamily: 'Poppins',
							weight: '600',
							size: '22',
							lineHeight: 'normal',
							color: '#FFFFFF',
						},
					},
				},
			},
		],
		styleFields: [
			{
				key: 'gradientFrom',
				label: 'Gradient start',
				type: 'color',
				variants: [ 'gradient' ],
			},
			{
				key: 'gradientTo',
				label: 'Gradient end',
				type: 'color',
				variants: [ 'gradient' ],
			},
			{
				key: 'glassFrom',
				label: 'Tint start (20% opacity)',
				type: 'color',
				variants: [ 'glass' ],
			},
			{
				key: 'glassTo',
				label: 'Tint end (20% opacity)',
				type: 'color',
				variants: [ 'glass' ],
			},
			{
				key: 'bgColor',
				label: 'Background',
				type: 'color',
				variants: [ 'solid', 'green', 'bordered' ],
			},
			{
				key: 'borderColor',
				label: 'Border',
				type: 'color',
				variants: [ 'outline', 'bordered' ],
			},
			{
				key: 'hoverBgColor',
				label: 'Background',
				type: 'color',
				group: 'Hover',
				variants: [ 'bordered' ],
			},
			{
				key: 'hoverTextColor',
				label: 'Text color',
				type: 'color',
				group: 'Hover',
				variants: [ 'bordered' ],
			},
			{
				key: 'width',
				label: 'Min width (0 = fit text)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 400,
			},
			{
				key: 'height',
				label: 'Min height (0 = fit text)',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 120,
			},
			{
				key: 'radius',
				label: 'Corner radius',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 40,
			},
			paddingField( 'Padding', {
				gradient: '0 20 0 20',
				solid: '8 16 8 16',
				green: '0 24 0 24',
				bordered: '0 30 0 30',
				outline: '3 31 3 31',
				glass: '7 10 7 10',
			} ),
			marginField(),
			hoverField( 'hoverBg', 'Background', CTA_SIMPLE_HOVER ),
			hoverField( 'hoverText', 'Text color', CTA_SIMPLE_HOVER ),
			hoverField( 'hoverBorder', 'Border', [ 'outline', 'bordered' ] ),
			{
				key: 'icon',
				label: 'Icon',
				type: 'select',
				group: 'Icon',
				options: CTA_ICON_OPTIONS,
				iconOptions: true,
			},
			{
				key: 'iconPosition',
				label: 'Icon position',
				type: 'select',
				group: 'Icon',
				options: [
					{ value: 'right', label: 'After the text' },
					{ value: 'left', label: 'Before the text' },
				],
				hideWhen: { key: 'icon', values: [ '', 'none' ] },
			},
			{
				key: 'iconSize',
				label: 'Icon size (empty = text size)',
				type: 'number',
				unit: 'px',
				min: 8,
				max: 64,
				group: 'Icon',
				hideWhen: { key: 'icon', values: [ '', 'none' ] },
			},
			{
				key: 'iconGap',
				label: 'Space to text',
				type: 'number',
				unit: 'px',
				min: 0,
				max: 40,
				group: 'Icon',
				hideWhen: { key: 'icon', values: [ '', 'none' ] },
			},
		],
		textFields: [ { key: 'cta', label: 'Button Text', showLink: true } ],
		mediaFields: [],
	},
	{
		key: 'closeButton',
		label: 'Close Button',
		description: 'Lets visitors close the banner.',
		canHide: true,
		variants: [ { id: 'icon', label: 'Icon' } ],
		styleFields: [
			{ key: 'color', label: 'Icon color', type: 'color' },
			{
				key: 'size',
				label: 'Icon size',
				type: 'number',
				unit: 'px',
				min: 8,
				max: 32,
			},
			paddingField(
				'Padding (click area)',
				{ '*': '2 2 2 2' },
				{ max: 40 }
			),
			hoverField( 'hoverText', 'Icon color' ),
		],
		textFields: [],
		mediaFields: [],
	},
];

/**
 * @param key Component key.
 */
export const getComponentDefinition = ( key: string ) =>
	COMPONENTS.find( ( component ) => component.key === key );

/**
 * Choose a component's variant and apply its preset (style values and text
 * styles). Texts the admin wrote are kept; a text still set to another
 * variant's preset text takes the new one.
 *
 * @param design  Template.
 * @param key     Component.
 * @param variant Variant id.
 */
export const applyVariant = (
	design: TemplateDesign,
	key: ComponentKey,
	variant: string
): TemplateDesign => {
	const definition = COMPONENTS.find( ( item ) => item.key === key );
	const option = definition?.variants.find( ( item ) => item.id === variant );
	const component = design.components[ key ];
	const preset = option?.preset;

	const typography = { ...design.typography };
	if ( preset?.typography ) {
		( Object.keys( preset.typography ) as TextKey[] ).forEach(
			( textKey ) => {
				const { text, ...styles } =
					preset.typography?.[ textKey ] || {};
				const current = typography[ textKey ];
				const presetTexts = ( definition?.variants || [] )
					.map(
						( item ) => item.preset?.typography?.[ textKey ]?.text
					)
					.filter( Boolean );
				typography[ textKey ] = {
					...current,
					...styles,
					text:
						text !== undefined &&
						presetTexts.includes( current.text )
							? text
							: current.text,
				};
			}
		);
	}

	return {
		...design,
		components: {
			...design.components,
			[ key ]: {
				...component,
				variant,
				style: { ...component.style, ...( preset?.style || {} ) },
			},
		},
		typography,
	};
};
